import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { api, put } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { BankNameField } from '../../lib/BankNameField.js';
import { Alert, Spinner } from '../../lib/ui.js';
import { t } from '../../i18n/index.js';

export type BankDetails = {
  bank_name: string | null;
  bank_account_number: string | null;
  ifsc_code: string | null;
  pan_number: string | null;
};

/** The labels of whatever is not on record, in the order the form asks. */
export function missingBankLabels(b: BankDetails | null): string[] {
  if (!b) return [];
  const out: string[] = [];
  if (!b.bank_name) out.push(t('bank_name'));
  if (!b.bank_account_number) out.push(t('bank_account'));
  if (!b.ifsc_code) out.push(t('bank_ifsc'));
  if (!b.pan_number) out.push(t('bank_pan'));
  return out;
}

/**
 * Where the worker is paid.
 *
 * Asked for once, after the passkey is set up, and skippable — attendance is never blocked on
 * this, and somebody standing at the gate on their first morning should be punching, not reading
 * their passbook. A worker who skips is reminded on the punch screen and chased by the admin,
 * who sees it on their row; no pay slip is issued until all four fields are on record (the
 * owner's rule, 18 September 2026).
 *
 * The same screen edits it afterwards, from Profile. There is no approval step: the number
 * appears on that worker's own payment advice and nowhere else, so making them wait for a
 * signature to fix their own typo would be ceremony for its own sake.
 */
export function BankDetailsPage({ mode = 'onboarding' }: { mode?: 'onboarding' | 'edit' }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [account, setAccount] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [pan, setPan] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<BankDetails>('/me/bank');
      setName(r.bank_name ?? '');
      setAccount(r.bank_account_number ?? '');
      setIfsc(r.ifsc_code ?? '');
      setPan(r.pan_number ?? '');
    } catch {
      // Nothing saved yet, or no connection. Either way the form still works.
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The last step of joining: get the app.
   *
   * Always handed over, never second-guessed here. This used to skip when a localStorage flag
   * said the phone had seen the add-to-home-screen step, which is a different step and a
   * different question — every phone that had been through the old enrolment carried it, so the
   * worker was sent to the punch screen and never told how to install anything. Get The App
   * decides for itself whether it has something to say.
   */
  const onward = () => navigate('/get-the-app', { replace: true });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await put('/me/bank', {
        bank_name: name.trim(),
        bank_account_number: account.trim(),
        ifsc_code: ifsc.trim(),
        pan_number: pan.trim(),
      });
      setSaved(true);
      if (mode === 'onboarding') onward();
      else navigate('/profile', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <Spinner />;

  return (
    <section className="card">
      <h2>{t('bank_title')}</h2>
      <p>{mode === 'onboarding' ? t('bank_intro') : t('bank_intro_edit')}</p>
      {error && <Alert>{error}</Alert>}
      {saved && mode === 'edit' && <Alert kind="success">{t('bank_saved')}</Alert>}

      <form onSubmit={save} className="stack">
        <BankNameField value={name} onChange={setName} required />
        <label>
          {t('bank_account')}
          <input
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            required
            inputMode="numeric"
            maxLength={24}
            autoComplete="off"
            placeholder={t('bank_account_hint')}
          />
          <span className="muted block-help">{t('bank_account_help')}</span>
        </label>
        <label>
          {t('bank_ifsc')}
          <input
            value={ifsc}
            onChange={(e) => setIfsc(e.target.value.toUpperCase())}
            required
            maxLength={11}
            autoCapitalize="characters"
            autoComplete="off"
            placeholder={t('bank_ifsc_hint')}
          />
          <span className="muted block-help">{t('bank_ifsc_help')}</span>
        </label>
        <label>
          {t('bank_pan')}
          <input
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase())}
            required
            maxLength={10}
            autoCapitalize="characters"
            autoComplete="off"
            placeholder={t('bank_pan_hint')}
          />
          <span className="muted block-help">{t('bank_pan_help')}</span>
        </label>

        <div className="row">
          <button
            type="submit"
            disabled={busy || !name.trim() || !account.trim() || !ifsc.trim() || !pan.trim()}
          >
            {busy ? t('bank_saving') : t('bank_save')}
          </button>
          {mode === 'onboarding' && (
            <button type="button" className="secondary" onClick={onward} disabled={busy}>
              {t('bank_later')}
            </button>
          )}
        </div>
      </form>

      <p className="muted">
        <Landmark size={16} aria-hidden="true" /> {t('bank_privacy')}
      </p>
    </section>
  );
}
